import * as React from 'react';
import { toast as sonnerToast, Toaster as SonnerToaster } from 'sonner';
import { cn } from '../../lib/cn';

/**
 * Toast — Typed sonner wrapper styled with NV glass surfaces.
 *
 * Provides a `toast()` function (plus `.success`, `.error`, `.warning`,
 * `.info` convenience methods) and a `<Toaster />` mount point.
 *
 * The toast surface matches the `glass-strong nv-toast` pattern from the
 * design system `#toasts` spec — a frosted glass backdrop with a coloured
 * icon badge per variant.
 *
 * Reference: docs/design-system/index.html #toasts
 */

export type ToastVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  default: 'glass-strong',
  success: 'bg-[rgba(21,128,61,0.08)] border border-[rgba(21,128,61,0.2)]',
  warning: 'bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.25)]',
  danger: 'bg-[rgba(220,38,38,0.08)] border border-[rgba(220,38,38,0.2)]',
  info: 'glass-accent',
};

function toast({
  title,
  description,
  variant = 'default',
  duration,
}: ToastOptions) {
  sonnerToast(title, {
    description,
    duration,
    className: cn('nv-toast rounded-xl', VARIANT_CLASSES[variant]),
  });
}

toast.success = (
  title: string,
  opts?: Omit<ToastOptions, 'variant' | 'title'>,
) => {
  toast({ ...opts, title, variant: 'success' });
};

toast.error = (
  title: string,
  opts?: Omit<ToastOptions, 'variant' | 'title'>,
) => {
  toast({ ...opts, title, variant: 'danger' });
};

toast.warning = (
  title: string,
  opts?: Omit<ToastOptions, 'variant' | 'title'>,
) => {
  toast({ ...opts, title, variant: 'warning' });
};

toast.info = (
  title: string,
  opts?: Omit<ToastOptions, 'variant' | 'title'>,
) => {
  toast({ ...opts, title, variant: 'info' });
};

/**
 * Distance between the toast stack and the viewport edges. A number is px;
 * a string is any CSS length (including `calc()`/`var()`), so a consumer can
 * keep the stack clear of its own fixed chrome (e.g. a bottom tab bar) by
 * referencing that chrome's height token. The object form sets individual
 * sides; an omitted side keeps the default.
 */
export type ToasterOffset =
  | string
  | number
  | {
      top?: string | number;
      right?: string | number;
      bottom?: string | number;
      left?: string | number;
    };

export interface ToasterProps {
  position?:
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'top-center'
    | 'bottom-center';
  /** Viewport offset on wide screens (default 24px on every side). */
  offset?: ToasterOffset;
  /**
   * Viewport offset on narrow screens (the stack goes full-width below
   * 600px; default 16px on every side). Set it alongside `offset` when fixed
   * chrome is present on phones too.
   */
  mobileOffset?: ToasterOffset;
}

export function Toaster({
  position = 'bottom-right',
  offset,
  mobileOffset,
}: ToasterProps) {
  return (
    <SonnerToaster
      position={position}
      offset={offset}
      mobileOffset={mobileOffset}
    />
  );
}

export { toast };
